import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  Pagination,
  Skeleton,
  Chip,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import ConfirmationModal from "../Confirmation/ConfirmationModal";
import Navbar from "../homepage/Navbar";
import {Footer} from "../../shared";
import searchIcon from "../../assets/image/png/search.svg";
import { Header } from "../../shared";

const Orders = () => {
  const token = Cookies.get("token");
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isConfirmationPopup, setIsConfirmationPopup] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const ordersPerPage = 5;

  useEffect(() => {
    const storedUserId = Cookies.get("userId");
    if (storedUserId) {
      fetchOrders(storedUserId);
    } else {
      toast.error("User ID not found. Please log in.");
      setLoading(false);
    }
  }, []);

  const fetchOrders = async (userId) => {
    try {
      console.log("Fetching orders and applications for userId:", userId);

      const [orderRes, applicationRes, projectsRes, servicesRes] =
        await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/getOrder/${userId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(
            `${process.env.REACT_APP_API_URL}/getUserApplications/${userId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          ),
          axios.get(`${process.env.REACT_APP_API_URL}/getAllProjects`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/allDroneServices`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

      // Process regular product orders
      let orders = [];
      if (Array.isArray(orderRes.data.data)) {
        orders = orderRes.data.data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
      }

      const allApplications = applicationRes.data?.data || {};
      const allProjects = projectsRes.data?.projects || [];
      const allServices = servicesRes.data?.services || [];

      console.log("All Services:", allServices);

      // Create maps for quick lookup and existence check
      const projectMap = {};
      allProjects.forEach((project) => {
        projectMap[project._id] = project;
      });

      const serviceMap = {};
      allServices.forEach((service) => {
        serviceMap[service._id] = service;
      });

      // Process project applications - filter out those where project doesn't exist
      const projectApplications = (allApplications.projects || [])
        .filter((app) => projectMap[app.projectId?._id]) // Only keep apps with existing projects
        .map((app) => {
          const project = projectMap[app.projectId._id];
          return {
            ...app,
            type: "project",
            title: project.title || "Unnamed Project",
            price: Number(app.offeredPrice) || 0,
            images: project.images || [],
            createdAt: app.createdAt || new Date(),
            status: app.applicationStatus,
            projectDetails: {
              location: project.location,
              description: project.description,
              budget: project.budget,
              timeline: project.timeline,
            },
          };
        });

      // Process service applications - filter out those where service doesn't exist
      const serviceApplications = (allApplications.services || [])
        .filter((app) => serviceMap[app.serviceId?._id]) // Only keep apps with existing services
        .map((app) => {
          const service = serviceMap[app.serviceId._id];
          return {
            ...app,
            type: "service",
            title: service.serviceTitle || "Unnamed Service",
            price: Number(app.offeredPrice) || 0,
            images: service.images || [],
            createdAt: app.createdAt || new Date(),
            status: app.applicationStatus,
            serviceDetails: {
              serviceArea: service.serviceArea,
              serviceType: service.serviceType,
              pricePerUnit: service.pricePerUnit,
              unitType: service.unitType,
              droneModel: service.droneModel,
            },
          };
        });

      setOrders(orders);
      setProjects(projectApplications);
      setServices(serviceApplications);
      setAllOrders(orders);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch orders or applications.");
    } finally {
      setLoading(false);
    }
  };
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim() === "") {
      setOrders(allOrders);
    } else {
      const filteredOrders = allOrders.filter((order) => {
        const productTitle = order.productId?.name?.toLowerCase() || "";
        const alternativeTitle = order.productIdd?.price?.toLowerCase() || "";
        return (
          productTitle.includes(value.toLowerCase()) ||
          alternativeTitle.includes(value.toLowerCase())
        );
      });
      setOrders(filteredOrders);
    }
  };

  const handleOpenConfirmationModal = (orderId) => {
    setOrderToCancel(orderId);
    setIsConfirmationPopup(true);
  };

  const handleCancelOrder = async () => {
    if (orderToCancel) {
      try {
        const response = await axios.post(
          process.env.REACT_APP_API_URL + "/cancelOrder",
          { orderId: orderToCancel },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data.status === "success") {
          setOrders(
            orders.map((order) =>
              order._id === orderToCancel
                ? {
                    ...order,
                    Payment: "Ordercancelled",
                    refundStatus: "Refund Initiated",
                  }
                : order,
            ),
          );
          toast.success("Order cancelled successfully. Refund initiated.");
        } else {
          toast.error("Failed to cancel the order.");
        }
      } catch (error) {
        console.error("Error canceling order:", error);
        toast.error("Error processing cancellation. Please try again later.");
      } finally {
        setIsConfirmationPopup(false);
      }
    }
  };

const formatDate = (date) => {
  const dateObj = new Date(date);

  if (isNaN(dateObj.getTime())) {
    return "Invalid Date";
  }

  return dateObj.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const calculateDeliveryDate = (orderDate) => {
  const orderDateObj = new Date(orderDate);

  if (isNaN(orderDateObj.getTime())) {
    return "Invalid Date";
  }

  orderDateObj.setDate(orderDateObj.getDate() + 7);

  return formatDate(orderDateObj);
};

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "accepted":
        return "success";
      case "rejected":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  const paginatedOrders = orders.slice(
    (page - 1) * ordersPerPage,
    page * ordersPerPage,
  );

  if (loading) {
    return (
      <Box sx={{ paddingY: 5, backgroundColor: "#fff" }}>
        <Skeleton variant="text" width="30%" height={40} />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={100}
          sx={{ marginTop: 2 }}
        />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={100}
          sx={{ marginTop: 2 }}
        />
      </Box>
    );
  }

  return (
    <>
      <Header />
      <Box
        sx={{
          minHeight: "calc(100vh - 120px)",
          paddingY: 5,
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            margin: "0 auto",
            paddingX: { xs: 2, sm: 3, md: 4 },
          }}
        >
          {/* Header and Search Box */}
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ marginBottom: 3 }}
          >
            <Grid item>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", color: "#0A2447" }}
              >
                My Orders{" "}
                <span style={{ fontSize: "0.9rem", color: "#00A0D6" }}>
                  ({orders.length})
                </span>
              </Typography>
            </Grid>
            <Grid item>
              <TextField
                variant="outlined"
                placeholder="Search Product"
                value={searchTerm}
                onChange={handleSearch}
                size="small"
                sx={{
                  width: { xs: "100%", sm: 300 },
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#00A0D6",
                    },
                    "& input": {
                      boxShadow: "none",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <img
                      src={searchIcon}
                      alt="Search"
                      style={{ marginRight: 10, width: 20 }}
                    />
                  ),
                }}
              />
            </Grid>
          </Grid>

          {/* Regular Orders Section */}
          {orders.length > 0 && (
            <>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "#0A2447", marginBottom: 2 }}
              >
                Product Orders
              </Typography>
              {paginatedOrders.map((order) => (
                <Box
                  key={order._id}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: 3,
                    marginBottom: 2,
                    backgroundColor: "#fff",
                    borderRadius: 2,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
                    },
                    opacity:
                      order.Payment === "failed" ||
                      order.Payment === "in progress" ||
                      order.Payment === "Ordercancelled" ||
                      order.Payment === "OrderCancelledByManufacturer"
                        ? 0.6
                        : 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      flex: 1,
                    }}
                  >
                    <img
                      src={
                        order.productId?.images?.[0] ||
                        order.productIdd?.images?.[0] ||
                        "default_image_url"
                      }
                      alt="Product"
                      width={120}
                      height={120}
                      style={{ borderRadius: 8, objectFit: "cover" }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: "#0A2447" }}
                      >
                        {order.productId?.name ||
                          order.productIdd?.title ||
                          "Unknown Product"}
                      </Typography>
                      <Typography
                          variant="body2"
                          sx={{ color: "#666", marginTop: 1 }}
                        >
                          Ordered on: {formatDate(order.createdAt)}
                        </Typography>
                      {!["Ordercancelled", "failed", "in progress"].includes(order.Payment) &&
                        order.deliveryStatus?.toLowerCase() !== "delivered" && (
                          <Typography
                            variant="body2"
                            sx={{ color: "#666", marginTop: 1 }}
                          >
                            Delivery by: {calculateDeliveryDate(order.createdAt)}
                          </Typography>
                        )}
                      {["completed"].includes(order.Payment) && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#00A0D6",
                            fontWeight: "bold",
                            marginTop: 1,
                          }}
                        >
                          Delivery Status: {order.deliveryStatus}
                        </Typography>
                      )}

                      {order.Payment === "completed" &&
  order.deliveryStatus?.toLowerCase() === "shipped" &&
  order.trackingNumber && (
    <Typography
      variant="body2"
      sx={{
        color: "#2E7D32",
        fontWeight: "bold",
        marginTop: 1,
      }}
    >
      Tracking Number: {order.trackingNumber}
    </Typography>
)}
                    </Box>
                  </Box>

                  <Box sx={{ textAlign: "center", flex: 0.5 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        color:
                          order.Payment === "completed" ? "#00A0D6" : "red",
                        fontWeight: "bold",
                      }}
                    >
                      {order.Payment === "completed"
                        ? "Payment Completed"
                        : order.Payment}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      textAlign: "right",
                      flex: 0.5,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#000", fontWeight: "bold" }}
                    >
                      ₹{(order.total || order.price || 0).toFixed(2)}
                    </Typography>

                    {order.Payment === "completed" &&
                    !["shipped", "delivered"].includes(
                      order.deliveryStatus?.toLowerCase()
                    ) && (
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenConfirmationModal(order._id);
                        }}
                        sx={{ marginTop: 1 }}
                      >
                        Cancel Order
                      </Button>
                  )}
                  </Box>
                </Box>
              ))}

              {/* Pagination for orders */}
              <Box
                sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}
              >
                <Pagination
                  count={Math.ceil(orders.length / ordersPerPage)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            </>
          )}

          {/* Projects Applications Section */}
          {projects.length > 0 && (
            <>
              <Divider sx={{ my: 4 }} />
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "#0A2447", marginBottom: 2 }}
              >
                Project Applications
              </Typography>
              {projects.map((project) => (
                <Box
                  key={project._id}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: 3,
                    marginBottom: 2,
                    backgroundColor: "#fff",
                    borderRadius: 2,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      flex: 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: "#0A2447" }}
                      >
                        {project.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", marginTop: 1 }}
                      >
                        Applied on:{" "}
                        {new Date(project.createdAt).toLocaleDateString()}
                      </Typography>
                      {project.projectDetails && (
                        <>
                          <Typography
                            variant="body2"
                            sx={{ color: "#666", marginTop: 1 }}
                          >
                            Location: {project.projectDetails.location?.city},{" "}
                            {project.projectDetails.location?.state}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#666", marginTop: 1 }}
                          >
                            Project Budget: ₹
                            {project.projectDetails.budget?.toLocaleString() ||
                              "Not specified"}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#666", marginTop: 1 }}
                          >
                            Timeline:{" "}
                            {project.projectDetails.timeline || "Not specified"}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ textAlign: "center", flex: 0.5 }}>
                    <Chip
                      label={project.status}
                      color={getStatusColor(project.status)}
                      sx={{ fontWeight: "bold", textTransform: "capitalize" }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 1, color: "#666" }}
                    >
                      {project.status === "accepted"
                        ? "You will be contacted soon"
                        : project.status === "rejected"
                          ? "Your application was not selected"
                          : "Waiting for response"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      textAlign: "right",
                      flex: 0.5,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#000", fontWeight: "bold" }}
                    >
                      ₹{(project.price || 0).toFixed(2)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      Your Offer Price
                    </Typography>
                  </Box>
                </Box>
              ))}
            </>
          )}

          {/* Services Applications Section */}
          {services.length > 0 && (
            <>
              <Divider sx={{ my: 4 }} />
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "#0A2447", marginBottom: 2 }}
              >
                Service Applications
              </Typography>
              {services.map((service) => (
                <Box
                  key={service._id}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: 3,
                    marginBottom: 2,
                    backgroundColor: "#fff",
                    borderRadius: 2,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      flex: 1,
                    }}
                  >
                    {/* <img
        src={service.images?.[0] || "default_image_url"}
        alt="Service"
        width={120}
        height={120}
        style={{ borderRadius: 8, objectFit: "cover" }}
      /> */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: "#0A2447" }}
                      >
                        {service.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", marginTop: 1 }}
                      >
                        Applied on:{" "}
                        {new Date(service.createdAt).toLocaleDateString()}
                      </Typography>
                      {service.serviceDetails && (
                        <>
                          <Typography
                            variant="body2"
                            sx={{ color: "#666", marginTop: 1 }}
                          >
                            Service Type:{" "}
                            {service.serviceDetails.serviceType ||
                              "Not specified"}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#666", marginTop: 1 }}
                          >
                            Location: {service.serviceDetails.serviceArea?.city}
                            , {service.serviceDetails.serviceArea?.state}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#666", marginTop: 1 }}
                          >
                            Rate: ₹
                            {service.serviceDetails.pricePerUnit?.toLocaleString() ||
                              "0"}{" "}
                            {service.serviceDetails.unitType || "unit"}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#666", marginTop: 1 }}
                          >
                            Drone Model:{" "}
                            {service.serviceDetails.droneModel ||
                              "Not specified"}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ textAlign: "center", flex: 0.5 }}>
                    <Chip
                      label={service.status}
                      color={getStatusColor(service.status)}
                      sx={{ fontWeight: "bold", textTransform: "capitalize" }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 1, color: "#666" }}
                    >
                      {service.status === "accepted"
                        ? "You will be contacted soon"
                        : service.status === "rejected"
                          ? "Your application was not selected"
                          : "Waiting for response"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      textAlign: "right",
                      flex: 0.5,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#000", fontWeight: "bold" }}
                    >
                      ₹{(service.price || 0).toFixed(2)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      Your Offer Price
                    </Typography>
                  </Box>
                </Box>
              ))}
            </>
          )}

          {/* Empty state */}
          {orders.length === 0 &&
            projects.length === 0 &&
            services.length === 0 && (
              <Typography
                variant="body1"
                sx={{ textAlign: "center", color: "#666" }}
              >
                No orders or applications found.
              </Typography>
            )}
        </Box>
      </Box>

      <ConfirmationModal
        show={isConfirmationPopup}
        onHide={() => setIsConfirmationPopup(false)}
        text="Are you sure you want to cancel this order?"
        onConfirm={handleCancelOrder}
      />

      <Footer />
    </>
  );
};

export default Orders;
